import { ConversionEventKind } from '@va/dashboard/shared/conversions';
import { useTranslate } from '@va/localization';
import { ComparisonFilter, SingleSelectFilter, TextFilter } from '@va/shared/feature-filters';
import { comparisonOperators, FilterOperators, percentComparisonOperators } from '@va/types/filters';
import { useMemo } from 'react';
import { FilterHook } from '../types';

export const useConversionNameFilter = (name?: string) => {
  const translate = useTranslate();
  return new TextFilter({
    id: 'name',
    name: name ?? 'name',
    label: translate(`filters.options.conversionEventName`),
  });
};

export const useConversionNoteFilter = (name?: string) => {
  const translate = useTranslate();
  return new TextFilter({
    id: 'note',
    name: name ?? 'note',
    label: translate('filters.options.conversionEventNote'),
  });
};

export const useConversionValueFilter = (name?: string) => {
  const translate = useTranslate();
  return new ComparisonFilter({
    id: 'conversionValue',
    name: name ?? 'conversionValue',
    label: translate('filters.options.conversionEventValue'),
    inputProps: { type: 'number' },
    operatorOptions: comparisonOperators,
  });
};

export const useConversionGoalFilter = (name?: string) => {
  const translate = useTranslate();
  return new ComparisonFilter({
    id: 'dailyConversionGoal',
    name: name ?? 'dailyConversionGoal',
    label: translate('filters.options.conversionEventGoal'),
    inputProps: { type: 'number' },
    operatorOptions: comparisonOperators,
  });
};

export const useConversionKindFilter = (name?: string) => {
  const translate = useTranslate();

  const eventKindOptions = useMemo(
    () =>
      Object.entries(ConversionEventKind).map(([, value]) => ({
        label: translate(`filters.conversionEventsKindOptions.${value}`),
        value: value,
      })),
    [translate],
  );

  return new SingleSelectFilter({
    id: 'kind',
    name: name ?? 'kind',
    label: translate('filters.options.conversionEventKind'),
    inputProps: {
      label: translate('filters.placeholder.conversionEventKind'),
      useOptions: () => ({
        data: eventKindOptions,
      }),
    },
  });
};

export const useCurrentConvertedSessionsFilter = (name?: string) => {
  const translate = useTranslate();
  return new ComparisonFilter({
    id: 'convertedSessions',
    name: name ?? 'convertedSessions',
    label: translate('filters.options.convertedSessions'),
    inputProps: { type: 'number' },
    operatorOptions: comparisonOperators,
  });
};

export const useCurrentConversionsFilter = (name?: string) => {
  const translate = useTranslate();
  return new ComparisonFilter({
    id: 'totalConversions',
    name: name ?? 'totalConversions',
    label: translate('filters.options.totalConversions'),
    inputProps: { type: 'number' },
    operatorOptions: comparisonOperators,
  });
};

export const useCurrentValueTotalFilter = (name?: string) => {
  const translate = useTranslate();
  return new ComparisonFilter({
    id: 'totalValue',
    name: name ?? 'totalValue',
    label: translate('filters.options.conversionValue'),
    inputProps: { type: 'number' },
    operatorOptions: comparisonOperators,
  });
};

export const useCurrentConversionRateFilter = (name?: string) => {
  const translate = useTranslate();
  return new ComparisonFilter({
    id: 'conversionRate',
    name: name ?? 'conversionRate',
    label: translate('filters.options.conversionRate'),
    inputProps: { type: 'number' },
    valueMapper(value) {
      return [value / 100];
    },
    operator: percentComparisonOperators[0],
    operatorOptions: percentComparisonOperators,
  });
};

export const useConversionEventIdFilter: FilterHook<typeof TextFilter> = (params) => {
  const translate = useTranslate();

  return new TextFilter({
    id: 'conversionEventId',
    name: 'conversionEventId',
    label: translate('filters.options.conversionEventId'),
    operator: FilterOperators.is,
    ...params,
  });
};
